import { matchesLabelNarrowing, type LabelNarrowing } from '@/features/labels';
import { deriveCardExecutionState } from './executionStatus';
import type { Board } from './boardModel';
import type { BoardStateFilter } from './searchParams';

/**
 * What a surface has been narrowed to. Every member absent means everything.
 *
 * One shape for all three surfaces that filter tasks. The tasks page had its
 * own `TaskSearchNarrowing` with these same three members, which is how the
 * board came to have no text search and Overview neither text nor state: the
 * type a page could express was the type its own feature package had declared.
 */
export interface TaskNarrowing {
  /** Trimmed; absent when empty. Matched against title *and* description. */
  q?: string;
  state?: BoardStateFilter | undefined;
  labels?: LabelNarrowing;
  /**
   * Excludes tasks a `linked_workflow` step created (`task.is_subtask`).
   * Subtasks are visible by default (tinted, not hidden) — this narrows them
   * back out for someone who wants an uncluttered view, the same way `state`
   * narrows by execution state. Unlike `includeHidden`, this is client-side:
   * the backend always returns subtasks now, so there is nothing to refetch.
   */
  excludeSubtasks?: boolean;
}

/** @deprecated The board's narrowing is every surface's narrowing. */
export type BoardNarrowing = TaskNarrowing;

/**
 * Whether a task's text contains every word of the query.
 *
 * Substrings, not fuzzy, and shared with the task list's row matcher so a query
 * typed on one page finds the same tasks on another. A workspace's task list is
 * small enough that an exact substring is predictable, and predictability is
 * the only thing a search box is judged on: a fuzzy match that quietly drops the
 * row someone was looking at is worse than no search at all.
 */
export function matchesTaskQuery(
  subject: {
    title: string;
    description?: string | null;
    /**
     * More text to search, for a surface that shows more about a task than its
     * board does — the Tasks page passes its id, workflow, step and creator
     * (spec 112 FR-009). A board passes none: there the workflow is the page
     * and the step is the column.
     */
    also?: readonly string[];
  },
  query: string | undefined,
): boolean {
  // Every word, anywhere (spec 112 FR-008). A single substring made "nightly
  // export" miss "The nightly export fell over" because the words were not
  // adjacent — the query someone types is the words they remember, not a
  // phrase. Each extra word can only narrow, so a one-word search finds
  // exactly what it did before.
  const words = query?.trim().toLowerCase().split(/\s+/).filter(Boolean) ?? [];
  if (words.length === 0) return true;
  const haystack = [
    subject.title,
    subject.description ?? '',
    ...(subject.also ?? []),
  ]
    .join('\n')
    .toLowerCase();
  return words.every((word) => haystack.includes(word));
}

/**
 * Narrows a board to the tasks matching every condition set, keeping every
 * column.
 *
 * Columns survive the filter deliberately: the board's shape is the workflow,
 * and hiding lanes because they hold nothing matching would make a filter look
 * like a different workflow. The counts in each header follow the filter, so a
 * lane reading "0" while filtered is telling the truth about what is shown.
 * Both rules now cover both conditions at once.
 *
 * Text, state and labels are **AND**ed, and applied in the one traversal. Two
 * passes would allocate a whole second board to answer the same question, and —
 * the reason that matters — would leave a window in which a caller could hold a
 * board narrowed by only some of the three.
 *
 * The same derivation the cards and the header summary use decides what each
 * state means, so all three can never disagree.
 */
export function filterBoard(board: Board, narrowing: TaskNarrowing): Board {
  const { q, state, labels, excludeSubtasks } = narrowing;
  // Nothing asked for, nothing copied: the identity return is what lets a
  // caller compare by reference to know the board is unfiltered.
  if (!q && !state && !labels && !excludeSubtasks) return board;

  return {
    ...board,
    columns: board.columns.map((column) => ({
      ...column,
      steps: column.steps.map((step) => ({
        ...step,
        tasks: step.tasks.filter((task) => {
          if (!matchesTaskQuery(task, q)) return false;
          if (!matchesLabelNarrowing(task, labels)) return false;
          if (excludeSubtasks && task.is_subtask) return false;
          if (!state) return true;
          const derived = deriveCardExecutionState(task, step);
          if (state === 'running') return derived.kind === 'live';
          if (state === 'waiting') return derived.kind === 'waiting-human';
          return derived.kind === 'failed';
        }),
      })),
    })),
  };
}
